"use client";
import type * as React from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { paramCase } from "@/lib/change-case";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------

type TDivClassName = Pick<React.HTMLAttributes<HTMLDivElement>, "className">;

interface CustomRHFSwitchProps<TFieldValues extends FieldValues>
  extends Omit<
      React.ComponentPropsWithoutRef<typeof Controller>,
      "name" | "control" | "render"
    >,
    Omit<
      React.ComponentPropsWithoutRef<typeof Switch>,
      "name" | "defaultValue" | "checked" | "onCheckedChange"
    > {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: TDivClassName;
  switchContainerClass?: TDivClassName;
  label?: string;
  labelProps?: React.ComponentPropsWithoutRef<typeof Label>;
}

export default function CustomRHFSwitch<TFieldValues extends FieldValues>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  switchContainerClass,
  label,
  labelProps,
  ...other
}: CustomRHFSwitchProps<TFieldValues>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({
        field: { onChange, value, ...fields },
        fieldState: { error },
      }) => (
        <div className={cn("space-y-2", containerClass)}>
          <div className={cn("flex items-center gap-2", switchContainerClass)}>
            <Switch
              id={other.id ?? paramCase(name)}
              {...fields}
              {...other}
              onCheckedChange={onChange}
              checked={value ?? false}
            />
            {!!label && (
              <Label
                {...labelProps}
                className={cn(
                  "h-4",
                  !!error && "text-error",
                  labelProps?.className,
                )}
                htmlFor={other.id ?? paramCase(name)}
              >
                {label}
              </Label>
            )}
          </div>
          {!!helperText && (
            <p className="text-xs leading-tight text-muted-foreground">
              {helperText}
            </p>
          )}
          {!!error && (
            <p className="text-xs leading-tight text-error">{error?.message}</p>
          )}
        </div>
      )}
    />
  );
}
